/**
 * The SSE event vocabulary — the ONE closed set of `crm_events` kinds.
 *
 * ## Why this lives in the contract rather than in either end
 *
 * The realtime substrate is `LISTEN`/`NOTIFY` (design §4.6): the worker writes
 * `NOTIFY crm_events, '{"userId":…,"kind":…}'`, an API task fans the frame out
 * over SSE as `event: <kind>`, and the web client subscribes per kind because
 * `EventSource.onmessage` fires ONLY for frames with no `event:` line.
 *
 * That makes `kind` a THREE-PARTY string: producer (worker), relay (api) and
 * consumer (web) must spell it identically or the frame is silently dropped —
 * no error, no log, just a UI that quietly stops updating. A free-form string
 * on the producer side is therefore a drift hazard with no failure signal,
 * which is exactly the shape of bug a contract package exists to remove.
 *
 * So: the tuple below is the union, `@crm/worker` types `CrmEvent.kind` against
 * it (and rejects a non-member at runtime, for callers that are not TypeScript),
 * and `@crm/web`'s `STREAM_INVALIDATIONS` is keyed by it so a new kind cannot be
 * added without deciding what it invalidates.
 *
 * ## Naming
 *
 * `snake_case`, no dots. The wire name is an SSE `event:` field and a Postgres
 * NOTIFY payload member; keeping one spelling convention across all seven means
 * the mapping between the three parties is identity rather than translation.
 */

import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.ts';

/**
 * Every kind the server may publish. Adding one is a deliberate act in three
 * places: here, the worker's publisher, and the web invalidation map (whose test
 * fails until the new kind is mapped).
 */
export const CRM_EVENT_KINDS = [
  /** The morning digest finished composing. Hermes v1. */
  'digest_ready',
  /** A recording finished local ASR and its transcript landed on the timeline. */
  'transcript_ready',
  /** An enrichment run completed (applied fields and/or new candidates). */
  'enrichment_done',
  /** A connected account's derived sync health changed (see `syncHealthSchema`). */
  'sync_health_changed',
  /** An ingest batch (extension capture, device push) was accepted and written. */
  'capture_landed',
  /** The recorder was armed for an upcoming meeting. */
  'meeting_armed',
  /** A contact's do-not-record suppression was added or lifted. */
  'do_not_record_changed',
] as const;

export type CrmEventKind = (typeof CRM_EVENT_KINDS)[number];

export const crmEventKindSchema = z.enum(CRM_EVENT_KINDS);

const KIND_SET: ReadonlySet<string> = new Set<string>(CRM_EVENT_KINDS);

/** Runtime membership check, for producers that are not typechecked against the union. */
export function isCrmEventKind(value: unknown): value is CrmEventKind {
  return typeof value === 'string' && KIND_SET.has(value);
}

/**
 * The NOTIFY payload / SSE frame body.
 *
 * Deliberately tiny: Postgres caps a NOTIFY payload at 8000 bytes and the design
 * makes the frame a NUDGE ("id + kind — the client refetches"), never a data
 * carrier. `refId` is whatever the kind's noun is (a digest id, a meeting id, a
 * contact id); the client uses it for targeted invalidation, never to render.
 */
export const crmEventSchema = z.object({
  kind: crmEventKindSchema,
  userId: uuidSchema,
  refId: uuidSchema.nullish(),
  orgId: uuidSchema.nullish(),
  at: isoDateTimeSchema.optional(),
});

export type CrmEventPayload = z.output<typeof crmEventSchema>;
